/**
 * Leaguemate Scouting — S1 discovery + foundation orchestrator.
 *
 * Produces the structured per-leaguemate object that S2 (affinity scoring)
 * will consume: identity, ranked similar leagues (with similarity + which were
 * sampled / marked for the S3 transaction set), and the raw affinity rosters.
 * NO fingerprint scoring here — that is S2/S3.
 *
 * Endpoint chain (v1 spec §3.1), all shapes confirmed against real cached
 * Sleeper JSON (2026-05-31):
 *   /league/{target}            → season + format profile (similarity target)
 *   /league/{target}/rosters    → owner_id per roster_id, players[]
 *   /league/{target}/users      → display_name, metadata.team_name
 *   /user/{uid}/leagues/nfl/{s} → their other leagues (carry format fields)
 *   /league/{src}/rosters       → the affinity datapoint (their roster there)
 */

import { scoutFetch, SCOUT_BASE_URL, resetScoutCacheStats, getScoutCacheStats } from "./scoutFetch.js";
import { toFormatProfile, rankAndCapLeagues } from "./scoutSimilarity.js";
import { SCOUT_SEASONS } from "./scoutConfig.js";
import { getSleeperPlayersMap } from "../sleeperImport.js";
import { fetchValueMap } from "./scoutValues.js";
import { buildManagerBelievers } from "./scoutAffinity.js";

const noop = () => {};

/**
 * FR1/FR2 step 1 — discover the leaguemates of a target league.
 * Returns the target league meta, the leaguemate identity list (entrant
 * excluded), and the roster_id → user_id map (needed by S3 for trades/FAAB).
 */
export async function discoverLeaguemates(targetLeagueId, { entrantUserId, onProgress = noop } = {}) {
  const lid = String(targetLeagueId).trim();

  onProgress("Loading target league…", 0, 1);
  const league = await scoutFetch(`${SCOUT_BASE_URL}/league/${lid}`, {
    cacheId: lid, season: "meta", dataType: "scout_league",
  });
  if (!league || !league.league_id) {
    throw new Error(`Target league ${lid} not found or unreadable`);
  }
  const season = league.season || SCOUT_SEASONS[0];

  const [rosters, users] = await Promise.all([
    scoutFetch(`${SCOUT_BASE_URL}/league/${lid}/rosters`, { cacheId: lid, season, dataType: "scout_rosters" }),
    scoutFetch(`${SCOUT_BASE_URL}/league/${lid}/users`,   { cacheId: lid, season, dataType: "scout_users" }),
  ]);

  const rosterArr = Array.isArray(rosters) ? rosters : [];
  const userArr   = Array.isArray(users) ? users : [];
  const userById  = new Map(userArr.map((u) => [u.user_id, u]));

  // roster_id → user_id (carry forward for S3 trade/FAAB attribution).
  const rosterIdToUserId = {};
  for (const r of rosterArr) rosterIdToUserId[r.roster_id] = r.owner_id ?? null;

  // Leaguemates = every roster owner except the entrant. Orphan rosters
  // (owner_id null) are skipped.
  const leaguemates = rosterArr
    .filter((r) => r.owner_id && r.owner_id !== entrantUserId)
    .map((r) => {
      const u = userById.get(r.owner_id) || {};
      const teamName = (u.metadata || {}).team_name; // often null → fall back
      return {
        user_id:      r.owner_id,
        display_name: u.display_name || r.owner_id,
        team_name:    teamName || u.display_name || null,
        roster_id:    r.roster_id,
      };
    });

  return {
    target: {
      league_id: league.league_id,
      name:      league.name || null,
      season,
      profile:   toFormatProfile(league),
    },
    leaguemates,
    rosterIdToUserId,
  };
}

/**
 * FR2 step 2 — for each leaguemate, fetch their other NFL leagues across the
 * configured seasons (current + prior) and dedupe by league_id. Cached per
 * (user_id, season). Returns Map<user_id, leagueObject[]>.
 */
export async function fetchLeaguemateLeagueSets(userIds, { seasons = SCOUT_SEASONS, onProgress = noop } = {}) {
  const out = new Map();
  let done = 0;
  await Promise.all(
    userIds.map(async (uid) => {
      const byId = new Map();
      for (const season of seasons) {
        const leagues = await scoutFetch(`${SCOUT_BASE_URL}/user/${uid}/leagues/nfl/${season}`, {
          cacheId: uid, season, dataType: "scout_user_leagues",
        });
        for (const l of Array.isArray(leagues) ? leagues : []) {
          if (l && l.league_id && !byId.has(l.league_id)) byId.set(l.league_id, l);
        }
      }
      out.set(uid, [...byId.values()]);
      onProgress(`Mapped leagues for ${++done}/${userIds.length} managers`, done, userIds.length);
    })
  );
  return out;
}

/**
 * S1 entry point. Given a target league, build the foundation object for
 * every leaguemate: identity + ranked similar leagues + raw affinity rosters.
 *
 * @returns {
 *   target: { league_id, name, season, profile },
 *   rosterIdToUserId,
 *   managers: Array<{
 *     user_id, display_name, team_name, roster_id,
 *     sampled:  Array<{ league_id, league_name, season, similarity, inTxSet }>,
 *     counts:   { candidates, aboveFloor, sampled, txSet },
 *     affinity: Array<{ league_id, league_name, season, similarity, inTxSet,
 *                       roster_id, players[], found }>  // raw rosters; S2 scores
 *   }>,
 *   cacheStats: { hits, misses }
 * }
 */
export async function buildScoutFoundation(
  targetLeagueId,
  { entrantUserId, onProgress = noop, computeAffinity = true, playersMap = null, valueMap = null } = {}
) {
  resetScoutCacheStats();

  const disc = await discoverLeaguemates(targetLeagueId, { entrantUserId, onProgress });
  const targetId = disc.target.league_id;
  const userIds = disc.leaguemates.map((m) => m.user_id);

  const leagueSets = await fetchLeaguemateLeagueSets(userIds, { onProgress });

  // Rank + cap each manager's source leagues. Exclude the target league
  // itself — we want their *other* leagues (§3.1), and their target roster is
  // already visible to the user.
  const managers = disc.leaguemates.map((m) => {
    const candidates = (leagueSets.get(m.user_id) || []).filter((l) => l.league_id !== targetId);
    const { sampled, counts } = rankAndCapLeagues(disc.target.profile, candidates);
    return { ...m, sampled, counts, _sampled: sampled };
  });

  // Affinity pull: one /rosters call per sampled league. Dispatched together
  // so the throttle queue (not us) controls concurrency/pacing; results
  // re-associated deterministically per manager.
  const totalLeagues = managers.reduce((n, m) => n + m._sampled.length, 0);
  let pulled = 0;
  const all = [];
  // forEach (not for-of) so the per-league closures aren't "declared in a loop"
  // (avoids no-loop-func on the shared `pulled` progress counter).
  managers.forEach((m) => {
    m._affinityPromises = m._sampled.map((s) => {
      const season = s.league.season || disc.target.season;
      const p = scoutFetch(`${SCOUT_BASE_URL}/league/${s.league_id}/rosters`, {
        cacheId: s.league_id, season, dataType: "scout_rosters",
      }).then((rosters) => {
        const arr = Array.isArray(rosters) ? rosters : [];
        const mine = arr.find((r) => r.owner_id === m.user_id);
        onProgress(`Pulled affinity rosters ${++pulled}/${totalLeagues}`, pulled, totalLeagues);
        const ls = s.league.settings || {};
        return {
          league_id:           s.league_id,
          previous_league_id:  s.league.previous_league_id ?? null, // for S2 lineage collapse
          league_name:         s.league.name || null,
          season,
          similarity:  s.similarity,
          inTxSet:     s.inTxSet,
          // Tier 0 category (§3.0) — free from the discovery payload; S3 uses
          // these to gate the dynasty/keeper TX set and the FAAB denominator.
          leagueType:   ls.type ?? null,           // 0 redraft · 1 keeper · 2 dynasty · 3 other
          bestBall:     ls.best_ball === 1,
          waiverBudget: ls.waiver_budget ?? 0,     // FAAB budget (denominator)
          roster_id:   mine?.roster_id ?? null,
          players:     mine?.players || [], // raw Sleeper player_ids; S2 resolves via players map
          found:       !!mine,
        };
      });
      all.push(p);
      return p;
    });
  });
  await Promise.all(all);

  for (const m of managers) {
    m.affinity = await Promise.all(m._affinityPromises);
    // Strip internal scaffolding + the bulky league objects from `sampled`
    // (the format fields S2/S3 need are already captured in counts/affinity).
    m.sampled = m._sampled.map((s) => ({
      league_id:   s.league_id,
      league_name: s.league.name || null,
      season:      s.league.season || disc.target.season,
      similarity:  s.similarity,
      inTxSet:     s.inTxSet,
      leagueType:  (s.league.settings || {}).type ?? null,
      bestBall:    (s.league.settings || {}).best_ball === 1,
    }));
    delete m._sampled;
    delete m._affinityPromises;
  }

  // S2 — affinity fingerprint (BELIEVERS ONLY; trade-away/net-acq is S3).
  // Resolve player_ids via the module-cached Sleeper players map and tag with
  // FantasyCalc values for the target league's format.
  let valueParams = null;
  if (computeAffinity) {
    onProgress("Loading player + value maps…", 0, 1);
    const pmap = playersMap || (await getSleeperPlayersMap());
    let vmap = valueMap;
    if (!vmap) {
      const fv = await fetchValueMap(disc.target.profile);
      vmap = fv.valueMap;
      valueParams = fv.params;
    }
    for (const m of managers) {
      const aff = buildManagerBelievers(m.affinity, pmap, vmap);
      m.believers = aff.believers;
      m.affinityMeta = {
        scope: "believers-only", // S3 adds trade-away / net-acquisition
        franchiseCount: aff.franchiseCount,
        usableFranchiseCount: aff.usableFranchiseCount,
        limitedData: aff.limitedData,
        confidence: aff.confidence,
      };
    }
  }

  return {
    target: disc.target,
    valueParams,
    rosterIdToUserId: disc.rosterIdToUserId,
    managers,
    cacheStats: getScoutCacheStats(),
  };
}
